import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { isHttpUrl } from "@/lib/utils";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Renders a small subset of Markdown that Gemini replies commonly use
 * (bold, links, line breaks) without pulling in a full markdown library
 * or using dangerouslySetInnerHTML. Anything not matched is shown as
 * plain text, so this never introduces raw HTML injection.
 */
const renderInlineTokens = (line) => {
  // Split on **bold** and [label](url) markdown, keeping the delimiters
  // so we can tell which pieces to style.
  const tokens = line.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return tokens.filter(Boolean).map((token, i) => {
    const boldMatch = token.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) return <strong key={i}>{boldMatch[1]}</strong>;
    const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      const isInternal = href.startsWith("/") && !href.startsWith("//");
      if (!isInternal && !isHttpUrl(href)) return <React.Fragment key={i}>{label}</React.Fragment>;
      return isInternal ? (
        <a key={i} href={href} className="underline hover:no-underline">{label}</a>
      ) : (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">{label}</a>
      );
    }
    return <React.Fragment key={i}>{token}</React.Fragment>;
  });
};

const renderMessageText = (text) => {
  const lines = String(text ?? "").split("\n");
  return lines.map((line, lineIdx) => {
    const bulletMatch = line.match(/^\s*[*-]\s+(.*)$/);
    const content = bulletMatch ? bulletMatch[1] : line;
    return (
      <React.Fragment key={lineIdx}>
        {bulletMatch && <span aria-hidden="true">• </span>}
        {renderInlineTokens(content)}
        {lineIdx < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

/**
 * Floating site assistant chatbot. Talks to POST /api/assistant/chat
 * (Gemini-backed, grounded in real catalog data via tool calls). Mounted
 * once in App.js so it's available on every public page.
 */
export const OPEN_ASSISTANT_EVENT = "hs:open-assistant";

export const ChatWidget = () => {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  // Below xl there is no floating button (it would cover content and ads);
  // the header's assistant button opens the panel with this event instead.
  useEffect(() => {
    const openPanel = () => setOpen(true);
    window.addEventListener(OPEN_ASSISTANT_EVENT, openPanel);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, openPanel);
  }, []);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    const nextMessages = [...messages, { role: "user", text }];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);
    try {
      const history = nextMessages.slice(0, -1).map(m => ({ role: m.role, text: m.text }));
      const { data } = await api.post("/assistant/chat", { message: text, history });
      setMessages(prev => [...prev, { role: "model", text: data.reply }]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setMessages(prev => [...prev, {
        role: "model",
        text: detail === "Assistant is not configured"
          ? (lang === "hi" ? "सहायक अभी उपलब्ध नहीं है।" : "The assistant isn't set up yet.")
          : (lang === "hi" ? "कुछ गड़बड़ हो गई। कृपया पुनः प्रयास करें।" : "Something went wrong. Please try again."),
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-2 right-2 z-50 sm:bottom-5 sm:right-5" data-testid="chat-widget">
      {open ? (
        <div role="dialog" aria-label={lang === "hi" ? "सहायक" : "Site Assistant"}
          className="w-[calc(100vw-1rem)] max-w-[380px] h-[min(500px,calc(100svh-5rem))] bg-card border border-border rounded-lg shadow-xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary text-primary-foreground">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Bot size={16} /> {lang === "hi" ? "सहायक" : "Site Assistant"}
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" aria-label="Close chat">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-sm text-muted-foreground text-center mt-8 px-4">
                {lang === "hi"
                  ? "पुस्तकें, लेखक या प्रकाशक खोजने में मदद चाहिए? पूछें!"
                  : "Ask me to help find books, authors, or publishers on the site."}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-md px-3 py-2 text-sm ${
                    m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {renderMessageText(m.text)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-secondary text-muted-foreground rounded-md px-3 py-2 text-sm">…</div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={lang === "hi" ? "अपना सवाल लिखें…" : "Type your question…"}
              disabled={loading}
              data-testid="chat-input"
            />
            <Button type="submit" size="icon" disabled={loading || !input.trim()} data-testid="chat-send-btn">
              <Send size={16} />
            </Button>
          </form>
        </div>
      ) : (
        <Button
          onClick={() => setOpen(true)}
          size="icon"
          className="hidden xl:inline-flex w-12 h-12 rounded-full shadow-lg"
          data-testid="chat-open-btn"
          aria-label="Open chat assistant"
        >
          <MessageCircle size={20} />
        </Button>
      )}
    </div>
  );
};
